import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import toBoolean from '@/utils/to-boolean'
import BlurredText from '@/components/blurred-text'
import ListingSection from '@/components/listing-section'
import { ParagraphRegular } from '@/components/text'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`

const InnerWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  background: ${colors.background};
  border-radius: 8px;
  padding: 14px 24px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`

const DetailItem = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 20px 0;
  position: relative;

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    border-bottom: solid 1px #eee;
  }

  &:nth-last-child(1),
  &:nth-last-child(2):nth-child(odd) {
    &:after {
      border-bottom: 0;
    }
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    &:nth-last-child(2):nth-child(odd) {
      &:after {
        border-bottom: solid 1px #eee;
      }
    }
  }
`

const DetailText = styled(ParagraphRegular)`
  color: ${colors.grey32};
  font-weight: 500;
  width: 150px;
`

const LeaseDetailsBlurred = ({
  listings,
  unit,
  className
}: {
  listings: { max_occupancy: number; listing_fees: any }
  unit: any
  className: string
}) => {
  const { t } = useTranslation()

  const getAdditionalFeatures = (features: any) => {
    const featureData = {
      parking: t('listing:b.listing.view.parking_available.text', 'Parking'),
      storage: t('listing:b.listing.view.storage.available.text', 'Storage')
    }

    return features.map(
      (feature: { fee_txt_id: any; fee_frequency_txt_id: string }) => {
        const featureId = feature.fee_txt_id
        if (feature.fee_frequency_txt_id === 'not_available') return null
        return (
          <DetailItem key={featureId}>
            <DetailText>
              {featureData[featureId as keyof typeof featureData]}
            </DetailText>
            <BlurredText>****</BlurredText>
          </DetailItem>
        )
      }
    )
  }

  return (
    <Wrapper className={className}>
      <ListingSection
        title={t('listing:b.listing.view.lease_details.text', 'Lease Details')}
        customGap="0px"
        showBottomSeparator
      >
        <InnerWrapper>
          <DetailItem>
            <DetailText>
              {t('listing:b.listing.view.move_in.text', 'Move-in Date')}
            </DetailText>
            <BlurredText>****</BlurredText>
          </DetailItem>
          <DetailItem>
            <DetailText>
              {t('listing:b.listing.view.lease_type.text', 'Lease Type')}
            </DetailText>
            <BlurredText>****</BlurredText>
          </DetailItem>
          {toBoolean(listings.max_occupancy) && (
            <DetailItem>
              <DetailText>
                {t(
                  'listing:b.listing.view.max_occupancy.text',
                  'Max. Occupancy'
                )}
              </DetailText>
              <BlurredText>****</BlurredText>
            </DetailItem>
          )}
          <DetailItem>
            <DetailText>
              {t('listing:b.listing.view.minimum_lease.title', 'Minimum Lease')}
            </DetailText>
            <BlurredText>****</BlurredText>
          </DetailItem>
          {getAdditionalFeatures(listings.listing_fees)}
          {toBoolean(unit.count_dens) && (
            <DetailItem>
              <DetailText>
                {t('listing:b.listing.view.den_flex_included.text', 'Den/Flex')}
              </DetailText>
              <BlurredText>****</BlurredText>
            </DetailItem>
          )}
        </InnerWrapper>
      </ListingSection>
    </Wrapper>
  )
}

export default LeaseDetailsBlurred
